import moment from 'moment';
import i18n from '../i18n';
import { BroadcastStatus } from '../interfaces';

const TODAY = moment();
const YESTERDAY = moment().subtract(1, 'day');
const TOMORROW = moment().add(1, 'day');
const ONE_WEEK_FORWARD = moment().add(7, 'd');

export function assetBroadcastStatus(
  { starttime, endtime }: { starttime: moment.Moment; endtime: moment.Moment },
  now: moment.Moment = moment(),
): BroadcastStatus {
  if (starttime.isAfter(now)) {
    return BroadcastStatus.Future; // ~ 2
  }

  if (starttime <= now && now < endtime) {
    return BroadcastStatus.Live; // ~ 1
  }

  return BroadcastStatus.Finished; // ~ 0
}

export function toReadableBroadcastDateString({
  date,
  ...options
}: {
  date: moment.Moment;
  longestFormat: string;
  longFormat: string;
  shortFormat: string;
  farInTheFutureFormat: string;
}) {
  if (!date.isValid()) {
    return '';
  }

  const currentYear = date.isSame(TODAY, 'year');
  const moreThanOneWeekForward = date.isSameOrAfter(ONE_WEEK_FORWARD, 'day');

  const when = YESTERDAY.isSame(date, 'day')
    ? i18n.t<string>('yesterday')
    : TODAY.isSame(date, 'day')
    ? i18n.t<string>('today')
    : TOMORROW.isSame(date, 'day')
    ? i18n.t<string>('tomorrow')
    : moreThanOneWeekForward
    ? ''
    : '';

  return when
    ? `${when} ${date.locale(i18n.language).format(options.shortFormat)}`
    : moreThanOneWeekForward
    ? date.locale(i18n.language).format(options.farInTheFutureFormat)
    : currentYear
    ? date.locale(i18n.language).format(options.longFormat)
    : date.locale(i18n.language).format(options.longestFormat);
}

function singularUnit(unit: moment.unitOfTime.Base) {
  const unitMap = {
    m: 'millisecond',
    ms: 'millisecond',
    millisecond: 'millisecond',
    milliseconds: 'millisecond',
    s: 'second',
    second: 'second',
    seconds: 'second',
    minute: 'minute',
    minutes: 'minute',
    h: 'hour',
    hour: 'hour',
    hours: 'hour',
    d: 'day',
    day: 'day',
    days: 'day',
    w: 'week',
    week: 'week',
    weeks: 'week',
    M: 'month',
    month: 'month',
    months: 'month',
    y: 'year',
    year: 'year',
    years: 'year',
  } as const;

  return unitMap[unit];
}

export function isWithinPast(unit: moment.unitOfTime.Base, startTime: moment.MomentInput, inclusive = false) {
  const now = moment();
  const start = moment(startTime);

  const timeAgo = now.clone().subtract(1, singularUnit(unit));
  return start.isBetween(timeAgo, now) || start.isSame(timeAgo, inclusive ? singularUnit(unit) : undefined);
}

export function isWithinFuture(unit: moment.unitOfTime.Base, startTime: moment.MomentInput, inclusive = false) {
  const now = moment();
  const start = moment(startTime);

  const inTime = now.clone().add(1, singularUnit(unit));
  return start.isBetween(now, inTime) || start.isSame(inTime, inclusive ? singularUnit(unit) : undefined);
}
